import {
    EmploymentType,
    EpisodeStatus,
    OrderType,
    VisitDatetimeField,
    VisitOutcome,
} from '@axle-health/graphql-schema';

import { HH_CONTAINER_KEYS } from '../containerKeys';
import {
    addCalendarDays,
    date,
    dateInDays,
    startOfDayInZone,
    startOfMedicareWeek,
} from '../graphInputHelpers';
import { BuilderContext, FilterInputByEntity, QueueDefinition, RootEntity } from '../types';
import { DEFAULT_CONTAINER_FILTER_WINDOW_DAYS } from './pinnacleServiceIds';

/**
 * The home health demo org, running Pinnacle's queues.
 *
 * The filters read services by name rather than by external ID, because a demo org's
 * services carry no external IDs to key on. Every name below is spelled exactly as the
 * org's own service list spells it: a name matching no row matches no visits, and
 * nothing warns about it.
 *
 * The demo catalog is one service per discipline per event, where Pinnacle's WellSky
 * catalog splits each event across billable, non-billable and death variants and adds
 * a layer of orders and paperwork tasks. Three consequences run through the file:
 *
 *   - Pinnacle's discharge, death and non-billable-discharge codes all collapse onto
 *     the one Agency Discharge service per discipline.
 *   - Every service this org has is a real visit, so Pinnacle's "and not the
 *     paperwork" exclusions have nothing to exclude and are dropped rather than
 *     approximated.
 *   - Pinnacle's order and authorization queues have no service or sync state to key
 *     on at all, so they are not published here. The three supervisory queues are
 *     published with their service lists left empty, which holds them at no rows.
 *
 * Where a queue keeps a key spelled for an earlier label — `hhExpiringFulfillmentWindow`
 * on Urgent Visits Needing Scheduling, `hhMultipleOasisPerEpisode` on Conflicting
 * Discharge and Recert Visits — the key stays as it is because snooze rows and per-user
 * container preferences are stored against it.
 *
 * Every queue is declared in full here, including the ones whose filters happen to
 * match another org's. A queue is per-org configuration, so changing what this org
 * counts means editing this file and no other.
 */

/** Services that open an episode. */
const SOC_SERVICE_NAMES = ['OT SOC', 'PT SOC', 'SN SOC', 'ST SOC'];

/** Services that resume an episode, including the combined resumption and recert. */
const ROC_SERVICE_NAMES = [
    'OT ROC',
    'OT ROC/Recert',
    'PT ROC',
    'PT ROC/Recert',
    'SN ROC',
    'SN ROC/Recert',
    'ST ROC',
    'ST ROC/Recert',
];

/** Services that recertify an episode, including the combined resumption and recert. */
const RECERT_SERVICE_NAMES = [
    'OT Recert',
    'OT ROC/Recert',
    'PT Recert',
    'PT ROC/Recert',
    'SN Recert',
    'SN ROC/Recert',
    'ST Recert',
    'ST ROC/Recert',
];

/** Services that evaluate a patient into a discipline. */
const EVAL_SERVICE_NAMES = [
    'OT Initial Eval',
    'PT Initial Eval',
    'SN Initial Eval',
    'ST Initial Eval',
];

/** Services that discharge a patient from the agency. */
const AGENCY_DISCHARGE_SERVICE_NAMES = [
    'OT Agency Discharge',
    'PT Agency Discharge',
    'SN Agency Discharge',
    'ST Agency Discharge',
];

/** Services that discharge a patient from one discipline. */
const OT_DISCIPLINE_DISCHARGE_SERVICE_NAMES = ['OT Discipline Discharge'];
const PT_DISCIPLINE_DISCHARGE_SERVICE_NAMES = ['PT Discipline Discharge'];
const SN_DISCIPLINE_DISCHARGE_SERVICE_NAMES = ['SN Discipline Discharge'];
const ST_DISCIPLINE_DISCHARGE_SERVICE_NAMES = ['ST Discipline Discharge'];

const ALL_DISCIPLINE_DISCHARGE_SERVICE_NAMES = [
    ...OT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ...PT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ...SN_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ...ST_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
];

/** A completed Eval or ROC in the same discipline re-admits and nullifies a discharge. */
const DISCIPLINE_READMISSION_SERVICE_NAMES = [...EVAL_SERVICE_NAMES, ...ROC_SERVICE_NAMES];

/**
 * Services that satisfy the reassessment requirement: reassessments themselves, the
 * services that open, resume or recertify an episode, and agency discharge.
 *
 * Skilled nursing has no reassessment service in this org, so its arm of the re-eval
 * queue is satisfied only by the shared services.
 */
const REASSESSMENT_SERVICE_NAMES = [
    'OT Reassessment',
    'PT Reassessment',
    'ST Reassessment',
    ...SOC_SERVICE_NAMES,
    ...ROC_SERVICE_NAMES,
    ...RECERT_SERVICE_NAMES,
    ...EVAL_SERVICE_NAMES,
    ...AGENCY_DISCHARGE_SERVICE_NAMES,
];

/**
 * The visits the supervisory queues watch, and the supervisory visits that cover them.
 *
 * All six lists are empty. This org has no supervisory service at all, and no way to
 * name the visits that need supervising either: a single `SN Routine Visit` carries
 * both the RN and the LPN license, and `PT Routine Visit` carries both PT and PTA, so
 * no service tells an assistant's visit from a clinician's. An empty anchor list
 * matches no visits, so each queue holds no rows until services exist to name.
 */
const HHA_VISIT_SERVICE_NAMES: string[] = [];
const HHA_SUPERVISORY_SERVICE_NAMES: string[] = [];
const LPN_VISIT_SERVICE_NAMES: string[] = [];
const LPN_SUPERVISORY_SERVICE_NAMES: string[] = [];
const PTA_VISIT_SERVICE_NAMES: string[] = [];
const PT_THERAPY_SUPERVISORY_SERVICE_NAMES: string[] = [];
const OTA_VISIT_SERVICE_NAMES: string[] = [];
const OT_THERAPY_SUPERVISORY_SERVICE_NAMES: string[] = [];

/**
 * Services that resolve an on-hold (transferred) episode, so a visit carrying one is
 * not a pending visit to cancel. Intentionally excludes the ST and skilled-nursing
 * resumptions, matching Pinnacle.
 */
const TRANSFER_RESOLUTION_SERVICE_NAMES = [
    'OT ROC',
    'OT ROC/Recert',
    'PT ROC',
    'PT ROC/Recert',
    'OT Agency Discharge',
    'PT Agency Discharge',
    'SN Agency Discharge',
];

type TherapyDiscipline = 'PT' | 'OT' | 'ST';

const THERAPY_DISCIPLINE_DISCHARGE_SERVICE_NAMES: Record<TherapyDiscipline, string[]> = {
    PT: PT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    OT: OT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ST: ST_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
};

// The episode gate several queues share: Active, ending today or later.
const episodeActivePresentOrFuture = (now: Date, timezone: string) => ({
    status: { exact: EpisodeStatus.ACTIVE },
    endDate: { gte: date(now, timezone) },
});

// The wider gate: Active or Pending, ending today or later.
const episodeActivePendingPresentOrFuture = (now: Date, timezone: string) => ({
    status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
    endDate: { gte: date(now, timezone) },
});

// A future scheduled discharge in a therapy discipline. Patients already on their
// way out are excluded from the re-eval queue.
const pendingDischargeVisitFilter = (
    discipline: TherapyDiscipline,
    { now, timezone }: BuilderContext,
): FilterInputByEntity[RootEntity.VISIT] => ({
    services: {
        name: {
            inList: [
                ...AGENCY_DISCHARGE_SERVICE_NAMES,
                ...THERAPY_DISCIPLINE_DISCHARGE_SERVICE_NAMES[discipline],
            ],
        },
    },
    disciplines: { code: { exact: discipline } },
    outcome: { exact: VisitOutcome.NONE },
    effectiveDate: { gte: date(now, timezone) },
});

// Patients that need care teams assigned.
const INCOMPLETE_CARE_TEAM: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Team',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: {
            ...episodeActivePendingPresentOrFuture(now, timezone),
            hasVisits: {
                disciplineCoveredByCareTeam: { exact: false },
                outcome: { exact: VisitOutcome.NONE },
            },
        },
    }),
};

// SOC visits that still need a clinician or a date, on an episode ending today or
// later.
const SOC: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.soc,
    label: 'SOC (Start of Care)',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: SOC_SERVICE_NAMES } },
        needsAssignment: { exact: true },
        episode: episodeActivePendingPresentOrFuture(now, timezone),
    }),
};

// ROC visits that still need a clinician or a date, on an Active or On Hold episode
// ending today or later.
const ROC: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.roc,
    label: 'ROC (Resumption of Care)',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: ROC_SERVICE_NAMES } },
        needsAssignment: { exact: true },
        episode: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.ON_HOLD] },
            endDate: { gte: date(now, timezone) },
        },
    }),
};

// Eval visits that still need a clinician or a date.
const EVAL: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.eval,
    label: 'Eval',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: EVAL_SERVICE_NAMES } },
        needsAssignment: { exact: true },
        episode: {
            status: {
                inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING, EpisodeStatus.ON_HOLD],
            },
            endDate: { gte: date(now, timezone) },
        },
    }),
};

// Visits still needing assignment whose fulfillment window closes within a week.
const URGENT_VISITS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.expiringFulfillmentWindow,
    label: 'Urgent Visits Needing Scheduling',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        episode: episodeActivePresentOrFuture(now, timezone),
        needsAssignment: { exact: true },
        latestEndDatetime: {
            gte: date(now, timezone),
            lte: dateInDays(now, timezone, 7),
        },
    }),
};

// Episodes carrying at least one visit that still needs a service, clinician or date.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HH_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        ...episodeActivePresentOrFuture(now, timezone),
        hasVisits: {
            needsAssignment: { exact: true },
        },
    }),
};

// Frequency orders with more active visits than the order allows.
const OVERSCHEDULED_PATIENTS: QueueDefinition<RootEntity.ORDER> = {
    key: HH_CONTAINER_KEYS.overscheduledPatients,
    label: 'Overscheduled Patients',
    rootEntity: RootEntity.ORDER,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        orderType: { exact: OrderType.FREQUENCY },
        exceedsMaxActiveOccurrences: { exact: true },
        episode: episodeActivePresentOrFuture(now, timezone),
    }),
};

// Frequency orders with fewer active visits than the order requires.
const UNDERSCHEDULED_PATIENTS: QueueDefinition<RootEntity.ORDER> = {
    key: HH_CONTAINER_KEYS.underscheduledPatients,
    label: 'Frequency Review Needed',
    rootEntity: RootEntity.ORDER,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        orderType: { exact: OrderType.FREQUENCY },
        exceedsMinActiveOccurrences: { exact: true },
        episode: episodeActivePresentOrFuture(now, timezone),
    }),
};

// Pending visits assigned to a clinician who is no longer active.
const INACTIVE_CLINICIAN_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: { isActive: { exact: false } },
        episode: episodeActivePendingPresentOrFuture(now, timezone),
        effectiveDate: {
            gte: dateInDays(now, timezone, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
        },
    }),
};

// Agency-discharged patients who still have pending future visits. Rooted on visits
// so the count is pending visits to cancel, not patients. The discharge visits
// themselves are excluded, since those are expected to remain.
const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.dischargeWithPendingVisits,
    label: 'Agency Discharged Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: false } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: date(now, timezone) },
        NOT: {
            services: { name: { inList: AGENCY_DISCHARGE_SERVICE_NAMES } },
        },
    }),
};

// Pending visits left behind after a discipline discharge that a later
// same-discipline Eval or ROC has not re-admitted.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            sameDiscipline: true,
            filter: {
                services: { name: { inList: ALL_DISCIPLINE_DISCHARGE_SERVICE_NAMES } },
                outcome: { exact: VisitOutcome.COMPLETED },
                NOT: {
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        sameDiscipline: true,
                        filter: {
                            services: {
                                name: { inList: DISCIPLINE_READMISSION_SERVICE_NAMES },
                            },
                            outcome: { exact: VisitOutcome.COMPLETED },
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gtOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    ltOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Pending visits tied to a frequency order that has been discontinued.
const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits,
    label: 'Discontinued Frequencies with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        outcome: { exact: VisitOutcome.NONE },
        relatedOrder: {
            discontinuedAt: { lt: date(now, timezone) },
        },
    }),
};

// Pending future visits on an on-hold (transferred) episode, excluding the visits
// that would themselves resolve the transfer.
const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.transfers,
    label: 'Transferred Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: date(now, timezone) },
        episode: { status: { exact: EpisodeStatus.ON_HOLD } },
        NOT: {
            services: { name: { inList: TRANSFER_RESOLUTION_SERVICE_NAMES } },
        },
    }),
};

type UpcomingSupeWithin = { daysAfterPriorSupe: number } | { daysFromToday: number };

// A future scheduled, fully-assigned supervisory visit that already covers the
// patient. Coverage has two shapes: within N days after a prior supervisory visit
// (HHA and LPN cadence), or inside [today, today + N] (therapy cadence).
const buildUpcomingSupeCoverageFilter = (
    supervisoryServiceNames: string[],
    disciplineFilter: FilterInputByEntity[RootEntity.VISIT],
    upcomingSupeWithin: UpcomingSupeWithin,
    ctx: BuilderContext,
): FilterInputByEntity[RootEntity.VISIT] => {
    const today = date(ctx.now, ctx.timezone);
    const supervisoryServicesFilter = {
        services: { name: { inList: supervisoryServiceNames } },
    };
    if ('daysAfterPriorSupe' in upcomingSupeWithin) {
        return {
            ...supervisoryServicesFilter,
            ...disciplineFilter,
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: today },
            needsAssignment: { exact: false },
            relatedVisitCondition: {
                exists: true,
                samePatient: true,
                sameEpisode: false,
                filter: { ...supervisoryServicesFilter, ...disciplineFilter },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gteOffsetMinutes: -upcomingSupeWithin.daysAfterPriorSupe * 24 * 60,
                        lteOffsetMinutes: 0,
                    },
                ],
            },
        };
    }
    return {
        ...supervisoryServicesFilter,
        ...disciplineFilter,
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: {
            gte: today,
            lte: dateInDays(ctx.now, ctx.timezone, upcomingSupeWithin.daysFromToday),
        },
        needsAssignment: { exact: false },
    };
};

// A patient is pending a supervisory visit when they have a future anchor visit,
// no completed supervisory visit inside the look-back, and no future supervisory
// visit already covering them.
const pendingSupervisoryBranch = (
    {
        anchorServiceNames,
        supervisoryServiceNames,
        lookbackDays,
        upcomingSupeWithin,
        discipline,
    }: {
        anchorServiceNames: string[];
        supervisoryServiceNames: string[];
        lookbackDays: number;
        upcomingSupeWithin: UpcomingSupeWithin;
        discipline?: Extract<TherapyDiscipline, 'PT' | 'OT'>;
    },
    ctx: BuilderContext,
): FilterInputByEntity[RootEntity.PATIENT] => {
    const disciplineFilter: FilterInputByEntity[RootEntity.VISIT] = discipline
        ? { disciplines: { code: { exact: discipline } } }
        : {};
    const today = date(ctx.now, ctx.timezone);
    const upcomingSupeFilter = buildUpcomingSupeCoverageFilter(
        supervisoryServiceNames,
        disciplineFilter,
        upcomingSupeWithin,
        ctx,
    );
    return {
        isActive: { exact: true },
        hasVisits: {
            services: { name: { inList: anchorServiceNames } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: today },
        },
        NOT: {
            hasVisits: {
                services: { name: { inList: supervisoryServiceNames } },
                ...disciplineFilter,
                outcome: { exact: VisitOutcome.COMPLETED },
                effectiveDate: {
                    gte: dateInDays(ctx.now, ctx.timezone, -lookbackDays),
                },
            },
        },
        AND: {
            NOT: { hasVisits: upcomingSupeFilter },
            ...(discipline
                ? {
                      AND: {
                          NOT: { hasVisits: pendingDischargeVisitFilter(discipline, ctx) },
                      },
                  }
                : {}),
        },
    };
};

// Patients with a future HHA visit and no supervisory visit covering it.
const PENDING_HHA_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.pendingHhaSupervisory,
    label: 'Pending HHA Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        hasEpisodes: episodeActivePresentOrFuture(ctx.now, ctx.timezone),
        ...pendingSupervisoryBranch(
            {
                anchorServiceNames: HHA_VISIT_SERVICE_NAMES,
                supervisoryServiceNames: HHA_SUPERVISORY_SERVICE_NAMES,
                lookbackDays: 7,
                upcomingSupeWithin: { daysAfterPriorSupe: 14 },
            },
            ctx,
        ),
    }),
};

// Patients with a future LPN visit and no supervisory visit covering it.
const PENDING_LPN_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.pendingLpnSupervisory,
    label: 'Pending LPN Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        hasEpisodes: episodeActivePresentOrFuture(ctx.now, ctx.timezone),
        ...pendingSupervisoryBranch(
            {
                anchorServiceNames: LPN_VISIT_SERVICE_NAMES,
                supervisoryServiceNames: LPN_SUPERVISORY_SERVICE_NAMES,
                lookbackDays: 23,
                upcomingSupeWithin: { daysAfterPriorSupe: 30 },
            },
            ctx,
        ),
    }),
};

// Patients with a future PTA or COTA visit and no therapy supervisory visit
// covering it, in either discipline.
const PENDING_THERAPY_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.pendingTherapySupervisory,
    label: 'Pending Therapy Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        ...pendingSupervisoryBranch(
            {
                anchorServiceNames: PTA_VISIT_SERVICE_NAMES,
                supervisoryServiceNames: PT_THERAPY_SUPERVISORY_SERVICE_NAMES,
                lookbackDays: 23,
                upcomingSupeWithin: { daysFromToday: 7 },
                discipline: 'PT',
            },
            ctx,
        ),
        OR: {
            ...pendingSupervisoryBranch(
                {
                    anchorServiceNames: OTA_VISIT_SERVICE_NAMES,
                    supervisoryServiceNames: OT_THERAPY_SUPERVISORY_SERVICE_NAMES,
                    lookbackDays: 23,
                    upcomingSupeWithin: { daysFromToday: 7 },
                    discipline: 'OT',
                },
                ctx,
            ),
        },
    }),
};

// One discipline's branch of the re-eval queue. Each is a complete patient filter,
// and the queue ORs three together so any uncovered discipline lights the patient up.
const pendingReEvalBranchForDiscipline = (
    discipline: TherapyDiscipline,
    ctx: BuilderContext,
): FilterInputByEntity[RootEntity.PATIENT] => {
    const { now, timezone } = ctx;
    return {
        isActive: { exact: true },
        hasEpisodes: episodeActivePresentOrFuture(now, timezone),
        hasVisits: {
            disciplines: { code: { exact: discipline } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: date(now, timezone) },
        },
        AND: {
            // The cadence does not start until the patient has a completed visit in
            // this discipline on the current episode.
            hasVisits: {
                disciplines: { code: { exact: discipline } },
                outcome: { exact: VisitOutcome.COMPLETED },
                episode: episodeActivePresentOrFuture(now, timezone),
            },
            NOT: {
                hasVisits: {
                    services: { name: { inList: REASSESSMENT_SERVICE_NAMES } },
                    disciplines: { code: { exact: discipline } },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    effectiveDate: { gte: dateInDays(now, timezone, -23) },
                },
            },
            AND: {
                NOT: {
                    hasVisits: {
                        services: { name: { inList: REASSESSMENT_SERVICE_NAMES } },
                        disciplines: { code: { exact: discipline } },
                        outcome: { exact: VisitOutcome.NONE },
                        effectiveDate: {
                            gte: date(now, timezone),
                            lte: dateInDays(now, timezone, 7),
                        },
                        needsAssignment: { exact: false },
                    },
                },
                AND: {
                    NOT: { hasVisits: pendingDischargeVisitFilter(discipline, ctx) },
                },
            },
        },
    };
};

// Patients due a PT, OT or ST reassessment.
const PENDING_THERAPY_REEVALUATION: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.pendingReassessment,
    label: 'Pending Therapy Re-Evaluation Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: (ctx) => ({
        ...pendingReEvalBranchForDiscipline('PT', ctx),
        OR: {
            ...pendingReEvalBranchForDiscipline('OT', ctx),
            OR: {
                ...pendingReEvalBranchForDiscipline('ST', ctx),
            },
        },
    }),
};

// Patients nearing their episode end with no recert or discharge visit scheduled,
// and no ROC scheduled or completed in the surrounding window.
const UPCOMING_END_OF_EPISODE: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HH_CONTAINER_KEYS.pendingEndOfEpisode,
    label: 'Upcoming End of Episode',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        status: { exact: EpisodeStatus.ACTIVE },
        patient: { isActive: { exact: true } },
        endDate: {
            gte: date(now, timezone),
            lte: dateInDays(now, timezone, 10),
        },
        NOT: {
            hasVisits: {
                services: {
                    name: {
                        inList: [...AGENCY_DISCHARGE_SERVICE_NAMES, ...RECERT_SERVICE_NAMES],
                    },
                },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            },
        },
        AND: {
            NOT: {
                hasVisits: {
                    services: { name: { inList: ROC_SERVICE_NAMES } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    effectiveDate: {
                        gte: dateInDays(now, timezone, -5),
                        lte: dateInDays(now, timezone, 10),
                    },
                },
            },
        },
    }),
};

// Evals scheduled or completed before the patient's SOC.
const VISITS_SCHEDULED_BEFORE_SOC: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.evalsBeforeSocs,
    label: 'Visits Scheduled Before SOC',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: EVAL_SERVICE_NAMES } },
        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
        episode: episodeActivePendingPresentOrFuture(now, timezone),
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            filter: {
                services: { name: { inList: SOC_SERVICE_NAMES } },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                effectiveDate: { gte: date(now, timezone) },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    gtOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Episodes carrying more than one discharge or recert visit.
const CONFLICTING_DISCHARGE_AND_RECERT_VISITS: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HH_CONTAINER_KEYS.multipleOasisPerEpisode,
    label: 'Conflicting Discharge and Recert Visits',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        ...episodeActivePresentOrFuture(now, timezone),
        hasVisits: {
            services: {
                name: {
                    inList: [...AGENCY_DISCHARGE_SERVICE_NAMES, ...RECERT_SERVICE_NAMES],
                },
            },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            relatedVisitCondition: {
                exists: true,
                sameEpisode: true,
                filter: {
                    services: {
                        name: {
                            inList: [...AGENCY_DISCHARGE_SERVICE_NAMES, ...RECERT_SERVICE_NAMES],
                        },
                    },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
    }),
};

// Upcoming discharges that still have other visits scheduled after them.
const UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.upcomingDischargeWithFuturePendingVisits,
    label: 'Upcoming Discharge With Future Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: AGENCY_DISCHARGE_SERVICE_NAMES } },
        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
        episode: episodeActivePresentOrFuture(now, timezone),
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            filter: {
                outcome: { exact: VisitOutcome.NONE },
                effectiveDate: { gte: date(now, timezone) },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    gtOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Completed SOC, ROC or Eval visits with no linked order.
const COMPLETED_VISITS_MISSING_AN_ORDER: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.completedVisitsNotMappedToOrders,
    label: 'Completed Visits Missing an Order',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        outcome: { exact: VisitOutcome.COMPLETED },
        hasOrder: { exact: false },
        patient: { isActive: { exact: true } },
        episode: episodeActivePresentOrFuture(now, timezone),
        services: {
            name: {
                inList: [...SOC_SERVICE_NAMES, ...ROC_SERVICE_NAMES, ...EVAL_SERVICE_NAMES],
            },
        },
    }),
};

// Active patients with no pending visit.
const ACTIVE_PATIENTS_WITH_NO_VISITS: QueueDefinition<RootEntity.PATIENT> = {
    key: HH_CONTAINER_KEYS.noFutureAppointments,
    label: 'Active Patients with No Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: episodeActivePresentOrFuture(now, timezone),
        NOT: {
            hasVisits: {
                outcome: { exact: VisitOutcome.NONE },
            },
        },
    }),
};

// Episodes currently on hold, with today inside the episode window.
const ALL_TRANSFERRED_PATIENTS: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HH_CONTAINER_KEYS.allTransferredPatients,
    label: 'All Transferred Patients',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        patient: { isActive: { exact: true } },
        status: { exact: EpisodeStatus.ON_HOLD },
        isActiveByDate: { exact: true },
    }),
};

// Missed visits inside the current Medicare week, which runs Sunday to Saturday.
const MISSED_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.missedVisits,
    label: 'Missed Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => {
        const weekStart = startOfMedicareWeek(now, timezone);
        return {
            outcome: { exact: VisitOutcome.MISSED },
            effectiveDate: {
                gte: weekStart,
                lte: addCalendarDays(weekStart, 6),
            },
        };
    },
};

// Visits assigned to a clinician with no active shift on the visit's date, over a
// rolling week. The date window is end-inclusive (today .. today+6); the datetime
// window is end-exclusive on the boundary (today 00:00 .. today+7 00:00), which
// covers the same seven calendar days.
const VISITS_WITHOUT_SHIFTS: QueueDefinition<RootEntity.VISIT> = {
    key: HH_CONTAINER_KEYS.visitsWithoutActiveShifts,
    label: 'Visits Without Shifts',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        hasActiveShiftOnDate: { exact: false },
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: {
            employmentType: {
                inList: [EmploymentType.FULL_TIME, EmploymentType.PART_TIME],
            },
        },
        AND: {
            effectiveDate: {
                gte: date(now, timezone),
                lte: dateInDays(now, timezone, 6),
            },
            OR: {
                effectiveDate: { isNull: true },
                latestEndDatetime: {
                    gte: startOfDayInZone(date(now, timezone), timezone),
                    lt: startOfDayInZone(dateInDays(now, timezone, 7), timezone),
                },
            },
        },
    }),
};

export const HOME_HEALTH_QUEUES: QueueDefinition[] = [
    INCOMPLETE_CARE_TEAM,
    SOC,
    ROC,
    EVAL,
    URGENT_VISITS_NEEDING_SCHEDULING,
    PATIENTS_NEEDING_SCHEDULING,
    OVERSCHEDULED_PATIENTS,
    UNDERSCHEDULED_PATIENTS,
    INACTIVE_CLINICIAN_VISITS,
    AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS,
    DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS,
    DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS,
    TRANSFERRED_PATIENTS_WITH_PENDING_VISITS,
    PENDING_HHA_SUPERVISORY,
    PENDING_LPN_SUPERVISORY,
    PENDING_THERAPY_SUPERVISORY,
    PENDING_THERAPY_REEVALUATION,
    UPCOMING_END_OF_EPISODE,
    VISITS_SCHEDULED_BEFORE_SOC,
    CONFLICTING_DISCHARGE_AND_RECERT_VISITS,
    UPCOMING_DISCHARGE_WITH_FUTURE_PENDING_VISITS,
    COMPLETED_VISITS_MISSING_AN_ORDER,
    ACTIVE_PATIENTS_WITH_NO_VISITS,
    ALL_TRANSFERRED_PATIENTS,
    MISSED_VISITS,
    VISITS_WITHOUT_SHIFTS,
];
